/**
 * AffiliateManagerAssignmentPage — two-tab operator view.
 *
 * TAB 1 "Assignments": table listing each affiliate, their current manager,
 *   when the relationship was created, and a row action to change the
 *   manager via a combobox drawer. A primary "Bulk Assign" CTA opens a
 *   modal where the operator selects multiple affiliates + a single target
 *   manager.
 *
 * TAB 2 "Manager Workload": grid of manager cards showing affiliate count
 *   badge, avg response time, total YTD revenue, and a utilization bar
 *   (emerald < 20 / amber 20-40 / red > 40).
 *
 * Design tokens conform to apps/dash/DESIGN.md — emerald-500 primary,
 * emerald→sky headline gradient, zinc borders, light theme only.
 */
import { useCallback, useMemo, useState } from "react";
import {
	useAffiliateManagerAssignmentList,
	useAffiliateManagerAssignmentCreate,
	useAffiliateManagerAssignmentDelete,
	useAffiliateManagerRoster,
	useAffiliateManagerWorkload,
} from "./hooks";
import {
	DEFAULT_MANAGERS,
	formatCurrencyUsd,
	formatInteger,
	formatResponseMinutes,
	formatTimestamp,
	isValidId,
	utilizationLabel,
	type AssignmentView,
	type ManagerWorkload,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens
// ---------------------------------------------------------------------------

const CARD =
	"rounded-lg border border-zinc-200 bg-white shadow-sm";
const INPUT =
	"w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:outline-none placeholder:text-zinc-400";
const LABEL = "block text-sm font-medium text-zinc-700 mb-1";
const BTN_PRIMARY =
	"bg-emerald-500 hover:bg-emerald-600 text-white rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed";
const BTN_SECONDARY =
	"bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50 rounded-md px-4 py-2 text-sm font-medium";
const BTN_DANGER =
	"bg-white border border-red-200 text-red-700 hover:bg-red-50 rounded-md px-3 py-1.5 text-xs font-medium";
const BADGE_ACTIVE =
	"inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700";
const BADGE_DRAFT =
	"inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-zinc-100 text-zinc-600";

type TabKey = "assignments" | "workload";

export function AffiliateManagerAssignmentPage() {
	const [tab, setTab] = useState<TabKey>("assignments");
	const [bulkOpen, setBulkOpen] = useState(false);
	const [rowEdit, setRowEdit] = useState<AssignmentView | null>(null);

	const listQuery = useAffiliateManagerAssignmentList();
	const workloadQuery = useAffiliateManagerWorkload();
	const rosterQuery = useAffiliateManagerRoster();
	const assignMut = useAffiliateManagerAssignmentCreate();
	const deleteMut = useAffiliateManagerAssignmentDelete();

	const rows: readonly AssignmentView[] = listQuery.data ?? [];
	const workloads: readonly ManagerWorkload[] = workloadQuery.data ?? [];
	const managers: readonly string[] = rosterQuery.data ?? DEFAULT_MANAGERS;

	const handleRowReassign = useCallback(
		async (affiliateId: string, managerId: string) => {
			await assignMut.mutateAsync({
				affiliate_ids: [affiliateId],
				manager_id: managerId,
			});
			setRowEdit(null);
		},
		[assignMut],
	);

	const handleBulkAssign = useCallback(
		async (affiliateIds: string[], managerId: string) => {
			await assignMut.mutateAsync({
				affiliate_ids: affiliateIds,
				manager_id: managerId,
			});
			setBulkOpen(false);
		},
		[assignMut],
	);

	return (
		<main
			data-testid="affiliate-manager-assignment-page"
			className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-6 py-6"
		>
			<PageHeader onBulkOpen={() => setBulkOpen(true)} />
			<Tabs active={tab} onChange={setTab} />

			{tab === "assignments" ? (
				<AssignmentsTab
					rows={rows}
					loading={listQuery.isLoading}
					error={listQuery.error?.message ?? null}
					onEditRow={setRowEdit}
					onUnassign={(id) => deleteMut.mutate(id)}
					busy={deleteMut.isPending}
				/>
			) : (
				<WorkloadTab
					workloads={workloads}
					loading={workloadQuery.isLoading}
					error={workloadQuery.error?.message ?? null}
				/>
			)}

			{bulkOpen && (
				<BulkAssignDialog
					affiliates={rows.map((r) => r.affiliate_id)}
					managers={managers}
					pending={assignMut.isPending}
					onClose={() => setBulkOpen(false)}
					onSubmit={handleBulkAssign}
				/>
			)}

			{rowEdit && (
				<ChangeManagerDrawer
					row={rowEdit}
					managers={managers}
					pending={assignMut.isPending}
					onClose={() => setRowEdit(null)}
					onSubmit={(mid) => handleRowReassign(rowEdit.affiliate_id, mid)}
				/>
			)}
		</main>
	);
}

export default AffiliateManagerAssignmentPage;

// ---------------------------------------------------------------------------
// Header + tabs
// ---------------------------------------------------------------------------

function PageHeader(props: { onBulkOpen: () => void }) {
	return (
		<header className="flex items-start justify-between gap-4">
			<div>
				<p className="text-xs text-zinc-500 uppercase tracking-wider">
					Affiliates
				</p>
				<h1 className="text-2xl font-semibold bg-gradient-to-r from-emerald-500 to-sky-500 bg-clip-text text-transparent">
					Manager Assignment
				</h1>
				<p className="text-sm text-zinc-500 mt-1 max-w-2xl">
					Assign internal managers to affiliate accounts. Track who owns what
					and how balanced the load is across the team.
				</p>
			</div>
			<button
				type="button"
				onClick={props.onBulkOpen}
				className={BTN_PRIMARY}
				data-testid="bulk-assign-open"
			>
				Bulk Assign
			</button>
		</header>
	);
}

function Tabs(props: { active: TabKey; onChange: (t: TabKey) => void }) {
	const tabs: Array<{ key: TabKey; label: string }> = [
		{ key: "assignments", label: "Assignments" },
		{ key: "workload", label: "Manager Workload" },
	];
	return (
		<div className="flex border-b border-zinc-200" role="tablist">
			{tabs.map((t) => {
				const active = t.key === props.active;
				return (
					<button
						key={t.key}
						type="button"
						role="tab"
						aria-selected={active}
						onClick={() => props.onChange(t.key)}
						className={
							"px-4 py-2 text-sm " +
							(active
								? "text-emerald-700 border-b-2 border-emerald-500 font-medium"
								: "text-zinc-500 hover:text-zinc-700")
						}
						data-testid={`tab-${t.key}`}
					>
						{t.label}
					</button>
				);
			})}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Tab 1 — Assignments table
// ---------------------------------------------------------------------------

function AssignmentsTab(props: {
	rows: readonly AssignmentView[];
	loading: boolean;
	error: string | null;
	onEditRow: (row: AssignmentView) => void;
	onUnassign: (affiliateId: string) => void;
	busy: boolean;
}) {
	if (props.loading) {
		return (
			<div
				className={`${CARD} p-6 text-sm text-zinc-500`}
				data-testid="affiliate-manager-assignment-loading"
			>
				Loading assignments…
			</div>
		);
	}
	if (props.error) {
		return (
			<div
				className={`${CARD} p-6 text-sm text-red-600`}
				role="alert"
				data-testid="affiliate-manager-assignment-error"
			>
				{props.error}
			</div>
		);
	}
	if (props.rows.length === 0) {
		return (
			<div
				className={`${CARD} p-10 text-center`}
				data-testid="affiliate-manager-assignment-empty"
			>
				<div className="mx-auto w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center text-emerald-600">
					<span className="text-sm font-semibold">AM</span>
				</div>
				<h3 className="text-lg font-semibold text-zinc-900 mt-4">
					No affiliates yet
				</h3>
				<p className="text-sm text-zinc-500 mt-2 max-w-sm mx-auto">
					Once affiliates exist in the system they will appear here so you can
					assign a manager.
				</p>
			</div>
		);
	}
	return (
		<div className={`${CARD} overflow-hidden`}>
			<table
				className="w-full border-collapse text-sm"
				data-testid="assignments-table"
			>
				<thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
					<tr>
						<th className="px-4 py-3 text-left font-medium">Affiliate</th>
						<th className="px-4 py-3 text-left font-medium">
							Current manager
						</th>
						<th className="px-4 py-3 text-left font-medium">Assigned since</th>
						<th className="px-4 py-3 text-left font-medium">Last activity</th>
						<th className="px-4 py-3 text-right font-medium">Actions</th>
					</tr>
				</thead>
				<tbody>
					{props.rows.map((row) => (
						<tr
							key={row.affiliate_id}
							className="hover:bg-emerald-50/40 transition-colors"
							data-testid={`assignment-row-${row.affiliate_id}`}
						>
							<td className="px-4 py-3 border-t border-zinc-200 font-medium text-zinc-900">
								{row.affiliate_id}
							</td>
							<td className="px-4 py-3 border-t border-zinc-200">
								{row.manager_id ? (
									<span className={BADGE_ACTIVE}>{row.manager_id}</span>
								) : (
									<span className={BADGE_DRAFT}>Unassigned</span>
								)}
							</td>
							<td className="px-4 py-3 border-t border-zinc-200 text-zinc-700">
								{formatTimestamp(row.assigned_at)}
							</td>
							<td className="px-4 py-3 border-t border-zinc-200 text-zinc-700">
								{formatTimestamp(row.last_activity_at)}
							</td>
							<td className="px-4 py-3 border-t border-zinc-200 text-right">
								<div className="inline-flex gap-2">
									<button
										type="button"
										onClick={() => props.onEditRow(row)}
										className={BTN_SECONDARY}
										data-testid={`change-manager-${row.affiliate_id}`}
									>
										Change Manager
									</button>
									{row.manager_id ? (
										<button
											type="button"
											onClick={() => props.onUnassign(row.affiliate_id)}
											disabled={props.busy}
											className={BTN_DANGER}
											data-testid={`unassign-${row.affiliate_id}`}
										>
											Unassign
										</button>
									) : null}
								</div>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Tab 2 — Manager Workload grid
// ---------------------------------------------------------------------------

function WorkloadTab(props: {
	workloads: readonly ManagerWorkload[];
	loading: boolean;
	error: string | null;
}) {
	if (props.loading) {
		return (
			<div
				className={`${CARD} p-6 text-sm text-zinc-500`}
				data-testid="workload-loading"
			>
				Loading workload…
			</div>
		);
	}
	if (props.error) {
		return (
			<div
				className={`${CARD} p-6 text-sm text-red-600`}
				role="alert"
				data-testid="workload-error"
			>
				{props.error}
			</div>
		);
	}
	if (props.workloads.length === 0) {
		return (
			<div
				className={`${CARD} p-10 text-center text-sm text-zinc-500`}
				data-testid="workload-empty"
			>
				No managers configured.
			</div>
		);
	}
	return (
		<div
			className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
			data-testid="workload-grid"
		>
			{props.workloads.map((w) => (
				<ManagerCard key={w.manager_id} workload={w} />
			))}
		</div>
	);
}

function ManagerCard(props: { workload: ManagerWorkload }) {
	const w = props.workload;
	const barColor =
		w.utilization === "high"
			? "bg-red-500"
			: w.utilization === "medium"
				? "bg-amber-500"
				: "bg-emerald-500";
	const percent = Math.min(100, (w.affiliate_count / 50) * 100);
	return (
		<article
			className={`${CARD} p-6 flex flex-col gap-4`}
			data-testid={`manager-card-${w.manager_id}`}
		>
			<header className="flex items-center gap-3">
				<div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center font-semibold text-sm">
					{initials(w.manager_id)}
				</div>
				<div className="min-w-0 flex-1">
					<h3 className="text-base font-medium text-zinc-900 truncate">
						{w.manager_id}
					</h3>
					<p className="text-xs text-zinc-500 uppercase tracking-wider">
						{utilizationLabel(w.utilization)}
					</p>
				</div>
				<span
					className={BADGE_ACTIVE}
					data-testid={`manager-count-${w.manager_id}`}
				>
					{formatInteger(w.affiliate_count)} affiliates
				</span>
			</header>

			<div className="space-y-2">
				<div className="flex items-center justify-between text-xs text-zinc-500 uppercase tracking-wider">
					<span>Utilization</span>
					<span data-testid={`manager-util-${w.manager_id}`}>
						{utilizationLabel(w.utilization)}
					</span>
				</div>
				<div className="h-2 rounded-full bg-zinc-100 overflow-hidden">
					<div
						className={`h-full ${barColor} transition-all`}
						style={{ width: `${percent}%` }}
						data-testid={`manager-bar-${w.manager_id}`}
					/>
				</div>
			</div>

			<dl className="grid grid-cols-2 gap-4 text-sm">
				<div>
					<dt className="text-xs text-zinc-500 uppercase tracking-wider">
						Avg response
					</dt>
					<dd className="text-zinc-900 font-medium mt-1">
						{formatResponseMinutes(w.avg_response_minutes)}
					</dd>
				</div>
				<div>
					<dt className="text-xs text-zinc-500 uppercase tracking-wider">
						YTD revenue
					</dt>
					<dd className="text-zinc-900 font-medium mt-1">
						{formatCurrencyUsd(w.ytd_revenue_cents)}
					</dd>
				</div>
			</dl>

			<button
				type="button"
				className={BTN_SECONDARY + " self-start"}
				data-testid={`view-affiliates-${w.manager_id}`}
			>
				View affiliates
			</button>
		</article>
	);
}

function initials(s: string): string {
	const cleaned = s.replace(/[_.:-]+/g, " ").trim();
	const parts = cleaned.split(/\s+/).slice(0, 2);
	return parts
		.map((p) => p.charAt(0).toUpperCase())
		.join("")
		.slice(0, 2) || "?";
}

// ---------------------------------------------------------------------------
// Change-manager drawer (single row)
// ---------------------------------------------------------------------------

function ChangeManagerDrawer(props: {
	row: AssignmentView;
	managers: readonly string[];
	pending: boolean;
	onClose: () => void;
	onSubmit: (managerId: string) => void | Promise<void>;
}) {
	const [target, setTarget] = useState("");

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-label="Change manager"
			data-testid="change-manager-drawer"
			className="fixed inset-0 z-50 bg-black/40 flex items-stretch justify-end"
		>
			<div className="bg-white w-full max-w-md h-full shadow-xl flex flex-col">
				<header className="px-6 py-4 border-b border-zinc-200">
					<h2 className="text-base font-medium text-zinc-900">
						Change manager
					</h2>
					<p className="text-xs text-zinc-500 mt-1">
						Affiliate <span className="font-mono">{props.row.affiliate_id}</span>
					</p>
				</header>
				<div className="flex-1 overflow-auto px-6 py-5 space-y-4">
					<div>
						<label className={LABEL} htmlFor="change-manager-input">
							New manager id
						</label>
						<input
							id="change-manager-input"
							name="targetManager"
							list="change-manager-options"
							className={INPUT}
							value={target}
							onChange={(e) => setTarget(e.currentTarget.value)}
							placeholder="ops_alice"
							autoFocus
							data-testid="change-manager-input"
						/>
						<datalist id="change-manager-options">
							{props.managers.map((m) => (
								<option key={m} value={m} />
							))}
						</datalist>
						<p className="text-xs text-zinc-500 mt-1">
							Start typing to filter the team roster, or enter a custom id.
						</p>
					</div>
				</div>
				<footer className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-3">
					<button
						type="button"
						onClick={props.onClose}
						className={BTN_SECONDARY}
						data-testid="change-manager-cancel"
					>
						Cancel
					</button>
					<button
						type="button"
						onClick={() => props.onSubmit(target.trim())}
						disabled={props.pending || !isValidId(target.trim())}
						className={BTN_PRIMARY}
						data-testid="change-manager-submit"
					>
						{props.pending ? "Saving…" : "Reassign"}
					</button>
				</footer>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Bulk-assign dialog (multiple affiliates → single manager)
// ---------------------------------------------------------------------------

function BulkAssignDialog(props: {
	affiliates: readonly string[];
	managers: readonly string[];
	pending: boolean;
	onClose: () => void;
	onSubmit: (affiliateIds: string[], managerId: string) => void | Promise<void>;
}) {
	const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
	const [target, setTarget] = useState("");

	const toggle = useCallback((id: string) => {
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}, []);

	const selectAll = useCallback(() => {
		setSelected(new Set(props.affiliates));
	}, [props.affiliates]);

	const clearAll = useCallback(() => {
		setSelected(new Set());
	}, []);

	const canSubmit =
		selected.size > 0 && isValidId(target.trim()) && !props.pending;

	const selectedList = useMemo(() => Array.from(selected), [selected]);

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-label="Bulk assign"
			data-testid="bulk-assign-dialog"
			className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
		>
			<div className="bg-white rounded-lg shadow-xl max-w-2xl w-full flex flex-col max-h-[80vh]">
				<header className="px-6 py-4 border-b border-zinc-200">
					<h2 className="text-base font-medium text-zinc-900">
						Bulk assign manager
					</h2>
					<p className="text-xs text-zinc-500 mt-1">
						Assign one manager to many affiliates in a single pass.
					</p>
				</header>
				<div className="flex-1 overflow-auto px-6 py-5 space-y-5">
					<div>
						<label className={LABEL} htmlFor="bulk-assign-target">
							Target manager
						</label>
						<input
							id="bulk-assign-target"
							name="bulkTargetManager"
							list="bulk-manager-options"
							className={INPUT}
							value={target}
							onChange={(e) => setTarget(e.currentTarget.value)}
							placeholder="ops_alice"
							data-testid="bulk-assign-target-input"
						/>
						<datalist id="bulk-manager-options">
							{props.managers.map((m) => (
								<option key={m} value={m} />
							))}
						</datalist>
					</div>

					<div>
						<div className="flex items-center justify-between mb-2">
							<label className={LABEL}>
								Affiliates ({formatInteger(selected.size)} selected)
							</label>
							<div className="flex gap-2">
								<button
									type="button"
									className="text-xs text-emerald-700 hover:underline"
									onClick={selectAll}
									data-testid="bulk-assign-select-all"
								>
									Select all
								</button>
								<span className="text-zinc-300">|</span>
								<button
									type="button"
									className="text-xs text-zinc-500 hover:underline"
									onClick={clearAll}
									data-testid="bulk-assign-clear"
								>
									Clear
								</button>
							</div>
						</div>
						<ul className="border border-zinc-200 rounded-md divide-y divide-zinc-200 max-h-64 overflow-auto">
							{props.affiliates.map((a) => {
								const checked = selected.has(a);
								return (
									<li key={a} className="px-3 py-2">
										<label className="flex items-center gap-3 text-sm text-zinc-700 cursor-pointer">
											<input
												type="checkbox"
												className="accent-emerald-500"
												checked={checked}
												onChange={() => toggle(a)}
												data-testid={`bulk-assign-check-${a}`}
											/>
											<span className="font-mono text-xs">{a}</span>
										</label>
									</li>
								);
							})}
						</ul>
					</div>
				</div>
				<footer className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-3">
					<button
						type="button"
						onClick={props.onClose}
						className={BTN_SECONDARY}
						data-testid="bulk-assign-cancel"
					>
						Cancel
					</button>
					<button
						type="button"
						onClick={() => props.onSubmit(selectedList, target.trim())}
						disabled={!canSubmit}
						className={BTN_PRIMARY}
						data-testid="bulk-assign-submit"
					>
						{props.pending
							? "Assigning…"
							: `Assign ${formatInteger(selected.size)}`}
					</button>
				</footer>
			</div>
		</div>
	);
}
